import { HatElement } from '@/components/hat-element.js';
import { assetsClient } from '@/clients/assets-client.js';
import type { MaintenanceScheduleItem, MaintenanceItemUpdate } from '@/clients/assets-client.js';

const FREQ_UNITS: Record<string, string> = { D: 'day', W: 'week', M: 'month', Y: 'year' };
const NEW_THRESHOLD_MS = 5 * 60 * 1000;

function formatFrequency(value: number, unit: string): string {
    const label = FREQ_UNITS[unit] ?? unit;
    return `Every ${value} ${value === 1 ? label : label + 's'}`;
}

function formatDate(iso: string | null | undefined): string {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

class HatMaintenanceList extends HatElement {
    private _locationId: string | null = null;
    private _assetId: string | null = null;
    private _assetCreatedAt: string | null = null;
    private _items: MaintenanceScheduleItem[] = [];
    private _editingItemId: string | null = null;
    private _showAddForm = false;
    private _loading = true;

    static get observedAttributes() {
        return ['location-id', 'asset-id', 'asset-created-at'];
    }

    attributeChangedCallback(name: string, _old: string | null, val: string | null) {
        if (name === 'location-id') this._locationId = val;
        if (name === 'asset-id') this._assetId = val;
        if (name === 'asset-created-at') this._assetCreatedAt = val;
        if (this.isConnected && this._locationId && this._assetId) this._load();
    }

    connectedCallback() {
        super.connectedCallback();
        this._locationId = this.getAttribute('location-id');
        this._assetId = this.getAttribute('asset-id');
        this._assetCreatedAt = this.getAttribute('asset-created-at');
        this._bindEvents();
        if (this._locationId && this._assetId) this._load();
    }

    private async _load() {
        this._loading = true;
        this._render();
        try {
            this._items = await assetsClient.listMaintenanceSchedule(this._locationId!, this._assetId!);
        } catch (e) {
            console.error('Failed to load maintenance schedule', e);
        }
        this._loading = false;
        this._render();
    }

    private _isNew(item: MaintenanceScheduleItem): boolean {
        if (!item.seededAt || !this._assetCreatedAt) return false;
        const diff = new Date(item.seededAt).getTime() - new Date(this._assetCreatedAt).getTime();
        return diff > NEW_THRESHOLD_MS;
    }

    private _render() {
        this.root.innerHTML = `
            <style>
                :host { display: block; }

                .section {
                    background: #fff;
                    border-radius: 16px;
                    padding: 20px;
                    box-shadow: 0 4px 16px rgba(0,0,0,0.06);
                }

                .section-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 16px;
                }

                .section-header h3 {
                    margin: 0;
                    font-size: 15px;
                    font-weight: 600;
                }

                .add-btn {
                    font-size: 13px;
                    padding: 5px 12px;
                    border-radius: 8px;
                    border: 1px solid #ddd;
                    background: #f7f7f7;
                    cursor: pointer;
                }

                .add-btn:hover { background: #ececec; }

                .item {
                    border-bottom: 1px solid #f0f0f0;
                    padding: 10px 0;
                }

                .item:last-child { border-bottom: none; }

                .item-row {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    font-size: 14px;
                }

                .toggle {
                    position: relative;
                    width: 36px;
                    height: 20px;
                    flex-shrink: 0;
                }

                .toggle input { opacity: 0; width: 0; height: 0; }

                .slider {
                    position: absolute;
                    inset: 0;
                    background: #ccc;
                    border-radius: 20px;
                    cursor: pointer;
                    transition: background 0.2s;
                }

                .slider:before {
                    content: '';
                    position: absolute;
                    height: 14px;
                    width: 14px;
                    left: 3px;
                    top: 3px;
                    background: white;
                    border-radius: 50%;
                    transition: transform 0.2s;
                }

                .toggle input:checked + .slider { background: #4caf50; }
                .toggle input:checked + .slider:before { transform: translateX(16px); }

                .item-label {
                    flex: 1;
                    font-weight: 500;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }

                .new-badge {
                    font-size: 10px;
                    font-weight: 600;
                    background: #ff9800;
                    color: white;
                    padding: 2px 6px;
                    border-radius: 6px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .item-freq {
                    font-size: 13px;
                    color: #666;
                    min-width: 120px;
                }

                .item-next {
                    font-size: 13px;
                    color: #888;
                    min-width: 110px;
                }

                .item-actions {
                    display: flex;
                    gap: 6px;
                }

                .edit-btn, .delete-btn, .save-btn, .cancel-btn {
                    font-size: 12px;
                    padding: 4px 10px;
                    border-radius: 6px;
                    border: 1px solid #ddd;
                    background: #f7f7f7;
                    cursor: pointer;
                }

                .edit-btn:hover, .cancel-btn:hover { background: #ececec; }
                .save-btn { background: #e8f5e9; border-color: #a5d6a7; }
                .save-btn:hover { background: #c8e6c9; }
                .delete-btn { color: #c62828; border-color: #ef9a9a; background: #fff; }
                .delete-btn:hover { background: #ffebee; }

                .edit-form {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 8px;
                    align-items: center;
                    margin-top: 10px;
                    padding: 10px;
                    background: #f9f9f9;
                    border-radius: 8px;
                    font-size: 13px;
                }

                .edit-form label { color: #555; }

                .edit-form input[type="number"] {
                    width: 56px;
                    padding: 4px 6px;
                    border: 1px solid #ddd;
                    border-radius: 6px;
                    font-size: 13px;
                }

                .edit-form select {
                    padding: 4px 6px;
                    border: 1px solid #ddd;
                    border-radius: 6px;
                    font-size: 13px;
                    background: white;
                }

                .edit-form input[type="date"] {
                    padding: 4px 6px;
                    border: 1px solid #ddd;
                    border-radius: 6px;
                    font-size: 13px;
                }

                .clear-date-btn {
                    font-size: 12px;
                    padding: 3px 8px;
                    border-radius: 6px;
                    border: 1px solid #ddd;
                    background: #fff;
                    cursor: pointer;
                    color: #888;
                }

                .add-form {
                    margin-top: 14px;
                    padding: 14px;
                    background: #f9f9f9;
                    border-radius: 10px;
                    border: 1px dashed #ddd;
                    display: flex;
                    flex-wrap: wrap;
                    gap: 10px;
                    align-items: flex-end;
                    font-size: 13px;
                }

                .add-form .field { display: flex; flex-direction: column; gap: 4px; }
                .add-form label { color: #555; font-size: 12px; }

                .add-form input[type="text"] {
                    padding: 5px 8px;
                    border: 1px solid #ddd;
                    border-radius: 6px;
                    font-size: 13px;
                    min-width: 180px;
                }

                .add-form input[type="number"] {
                    width: 60px;
                    padding: 5px 8px;
                    border: 1px solid #ddd;
                    border-radius: 6px;
                    font-size: 13px;
                }

                .add-form select {
                    padding: 5px 8px;
                    border: 1px solid #ddd;
                    border-radius: 6px;
                    font-size: 13px;
                    background: white;
                }

                .submit-btn {
                    padding: 5px 14px;
                    border-radius: 6px;
                    border: none;
                    background: #1976d2;
                    color: white;
                    font-size: 13px;
                    cursor: pointer;
                }

                .submit-btn:hover { background: #1565c0; }

                .empty {
                    font-size: 14px;
                    color: #aaa;
                    padding: 8px 0;
                }

                .loading { font-size: 14px; color: #aaa; padding: 8px 0; }
            </style>

            <div class="section">
                <div class="section-header">
                    <h3>Maintenance Schedule</h3>
                    <button class="add-btn" data-action="toggle-add">+ Add Item</button>
                </div>

                ${this._loading ? `<div class="loading">Loading…</div>` : this._renderItems()}

                ${this._showAddForm ? this._renderAddForm() : ''}
            </div>
        `;

        this._applyEntitlements();
    }

    private _renderItems(): string {
        if (!this._items.length) {
            return `<div class="empty">No maintenance items.</div>`;
        }

        return this._items.map(item => {
            const id = item.itemId ?? '';
            const isEditing = this._editingItemId === id;
            const isNew = this._isNew(item);
            const isCustom = !item.templateCode;

            return `
                <div class="item" data-item-id="${id}">
                    <div class="item-row">
                        <label class="toggle">
                            <input type="checkbox" data-action="toggle" data-item-id="${id}" ${item.enabled ? 'checked' : ''}>
                            <span class="slider"></span>
                        </label>
                        <span class="item-label">
                            ${item.label ?? ''}
                            ${isNew ? `<span class="new-badge">New</span>` : ''}
                        </span>
                        <span class="item-freq">${formatFrequency(item.frequencyValue ?? 0, item.frequencyUnit ?? '')}</span>
                        <span class="item-next">${item.enabled ? formatDate(item.nextNotifiedDate) : '—'}</span>
                        <div class="item-actions">
                            <button class="edit-btn" data-action="${isEditing ? 'cancel-edit' : 'edit'}" data-item-id="${id}">
                                ${isEditing ? 'Cancel' : 'Edit'}
                            </button>
                            ${isCustom ? `<button class="delete-btn" data-action="delete" data-item-id="${id}">Delete</button>` : ''}
                        </div>
                    </div>
                    ${isEditing ? this._renderEditForm(item) : ''}
                </div>
            `;
        }).join('');
    }

    private _renderEditForm(item: MaintenanceScheduleItem): string {
        const customDateVal = item.customNextDate
            ? new Date(item.customNextDate).toISOString().split('T')[0]
            : '';

        return `
            <div class="edit-form">
                <label>Every</label>
                <input type="number" class="edit-freq-value" min="1" value="${item.frequencyValue ?? 1}">
                <select class="edit-freq-unit">
                    ${['D','W','M','Y'].map(u =>
                        `<option value="${u}" ${item.frequencyUnit === u ? 'selected' : ''}>${FREQ_UNITS[u]}</option>`
                    ).join('')}
                </select>
                <label>Custom next date:</label>
                <input type="date" class="edit-custom-date" value="${customDateVal}">
                ${item.customNextDate ? `<button class="clear-date-btn" data-action="clear-date">Clear</button>` : ''}
                <button class="save-btn" data-action="save-edit" data-item-id="${item.itemId ?? ''}">Save</button>
            </div>
        `;
    }

    private _renderAddForm(): string {
        return `
            <div class="add-form">
                <div class="field">
                    <label>Label</label>
                    <input type="text" class="add-label" placeholder="e.g. Clean gutters">
                </div>
                <div class="field">
                    <label>Every</label>
                    <input type="number" class="add-freq-value" min="1" value="1">
                </div>
                <div class="field">
                    <label>Unit</label>
                    <select class="add-freq-unit">
                        ${['D','W','M','Y'].map(u => `<option value="${u}">${FREQ_UNITS[u]}</option>`).join('')}
                    </select>
                </div>
                <button class="submit-btn" data-action="submit-add">Add</button>
                <button class="cancel-btn" data-action="cancel-add">Cancel</button>
            </div>
        `;
    }

    private _bindEvents() {
        this.root.addEventListener('click', async (e) => {
            const target = (e.target as HTMLElement).closest('[data-action]') as HTMLElement | null;
            if (!target) return;

            const action = target.dataset.action!;
            const itemId = target.dataset.itemId ?? '';

            if (action === 'toggle-add') {
                this._showAddForm = !this._showAddForm;
                this._render();
                return;
            }

            if (action === 'cancel-add') {
                this._showAddForm = false;
                this._render();
                return;
            }

            if (action === 'edit') {
                this._editingItemId = itemId;
                this._render();
                return;
            }

            if (action === 'cancel-edit') {
                this._editingItemId = null;
                this._render();
                return;
            }

            if (action === 'clear-date') {
                await this._updateItem(itemId, { clearCustomNextDate: true });
                return;
            }

            if (action === 'save-edit') {
                const form = (e.target as HTMLElement).closest('.edit-form')!;
                const freqValue = parseInt((form.querySelector('.edit-freq-value') as HTMLInputElement).value);
                const freqUnit = (form.querySelector('.edit-freq-unit') as HTMLSelectElement).value;
                const customDateInput = (form.querySelector('.edit-custom-date') as HTMLInputElement).value;
                const update: MaintenanceItemUpdate = {
                    frequencyValue: freqValue,
                    frequencyUnit: freqUnit,
                };
                if (customDateInput) {
                    update.customNextDate = new Date(customDateInput).toISOString() as any;
                }
                await this._updateItem(itemId, update);
                this._editingItemId = null;
                return;
            }

            if (action === 'delete') {
                await assetsClient.deleteMaintenanceItem(this._locationId!, this._assetId!, itemId);
                this._items = this._items.filter(i => i.itemId !== itemId);
                this._render();
                return;
            }

            if (action === 'submit-add') {
                const form = (e.target as HTMLElement).closest('.add-form')!;
                const label = (form.querySelector('.add-label') as HTMLInputElement).value.trim();
                const freqValue = parseInt((form.querySelector('.add-freq-value') as HTMLInputElement).value);
                const freqUnit = (form.querySelector('.add-freq-unit') as HTMLSelectElement).value;
                if (!label || !freqValue) return;

                const created = await assetsClient.createMaintenanceItem(
                    this._locationId!, this._assetId!,
                    { label, frequencyValue: freqValue, frequencyUnit: freqUnit }
                );
                this._items = [...this._items, created];
                this._showAddForm = false;
                this._render();
                return;
            }
        });

        this.root.addEventListener('change', async (e) => {
            const target = e.target as HTMLInputElement;
            if (target.dataset.action === 'toggle') {
                await this._updateItem(target.dataset.itemId!, { enabled: target.checked });
            }
        });
    }

    private async _updateItem(itemId: string, update: MaintenanceItemUpdate) {
        const updated = await assetsClient.updateMaintenanceItem(
            this._locationId!, this._assetId!, itemId, update
        );
        this._items = this._items.map(i => i.itemId === itemId ? updated : i);
        this._render();
    }
}

customElements.define('hat-maintenance-list', HatMaintenanceList);